import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { normalizeImageUrl, isGoogleDriveUrl } from '../lib/imageUrl';

const IMAGE_FIELD_KEYS = new Set(['image', 'image_url', 'photo', 'photo_url', 'avatar', 'logo', 'logo_url', 'thumbnail', 'thumbnail_url', 'banner', 'banner_url', 'cover', 'cover_url']);
const isImageField = (key) => IMAGE_FIELD_KEYS.has(String(key).toLowerCase());
import {
  LayoutDashboard, Settings, Home, Info, GraduationCap, Building2,
  ClipboardList, Tent, Users, FileText, Phone, Newspaper, Image,
  Upload, LogOut, Save, RefreshCw, ChevronRight, Globe, Eye, Briefcase, Download
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ============== AUTH ==============
function useAuth() {
  const [auth, setAuth] = useState(() => {
    const stored = sessionStorage.getItem('cms_auth');
    return stored ? JSON.parse(stored) : null;
  });
  const login = async (username, password) => {
    const res = await fetch(`${API}/api/admin/verify`, {
      method: 'POST',
      headers: { Authorization: 'Basic ' + btoa(`${username}:${password}`) },
    });
    if (res.ok) {
      const creds = { username, password };
      sessionStorage.setItem('cms_auth', JSON.stringify(creds));
      setAuth(creds);
      return true;
    }
    return false;
  };
  const logout = () => { sessionStorage.removeItem('cms_auth'); setAuth(null); };
  const headers = useMemo(
    () => (auth ? { Authorization: 'Basic ' + btoa(`${auth.username}:${auth.password}`) } : {}),
    [auth]
  );
  return { auth, login, logout, headers };
}

// ============== API HELPERS ==============
function useApi() {
  const { headers } = useAuth();
  const get = useCallback(async (url) => {
    const res = await fetch(`${API}${url}`, { headers });
    if (!res.ok) throw new Error(`GET ${url} failed`);
    return res.json();
  }, [headers]);
  const put = useCallback(async (url, data) => {
    const res = await fetch(`${API}${url}`, { method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
    if (!res.ok) throw new Error(`PUT ${url} failed`);
    return res.json();
  }, [headers]);
  const upload = useCallback(async (file, category, description) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', category);
    fd.append('description', description || '');
    const res = await fetch(`${API}/api/cms/upload`, { method: 'POST', headers, body: fd });
    if (!res.ok) throw new Error('Upload failed');
    return res.json();
  }, [headers]);
  return { get, put, upload };
}

// ============== LOGIN SCREEN ==============
function LoginScreen({ onLogin }) {
  const [u, setU] = useState('');
  const [p, setP] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await onLogin(u, p);
    setLoading(false);
    if (!ok) toast.error('Invalid credentials');
  };
  return (
    <div className="min-h-screen bg-[#1A202C] flex items-center justify-center p-4" data-testid="cms-login">
      <Card className="w-full max-w-md border-0 shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-[#4ECDC4] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Settings className="text-white" size={32} />
          </div>
          <CardTitle className="text-2xl font-bold text-[#1A202C]">SSMCH CMS</CardTitle>
          <p className="text-sm text-gray-500 mt-1">Content Management System</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div><Label>Username</Label><Input data-testid="cms-username" value={u} onChange={e => setU(e.target.value)} /></div>
            <div><Label>Password</Label><Input data-testid="cms-password" type="password" value={p} onChange={e => setP(e.target.value)} /></div>
            <Button data-testid="cms-login-btn" className="w-full bg-[#4ECDC4] hover:bg-[#3DBDB4]" disabled={loading}>{loading ? 'Signing in...' : 'Sign In'}</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ============== SIDEBAR ==============
const NAV_ITEMS = [
  { path: '', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { path: 'settings', icon: Settings, label: 'Global Settings' },
  { path: 'gallery', icon: Image, label: 'Campus Gallery' },
  { path: 'news', icon: Newspaper, label: 'News Articles' },
  { path: 'page/home', icon: Home, label: 'Home Page' },
  { path: 'page/about', icon: Info, label: 'About Page' },
  { path: 'page/academics', icon: GraduationCap, label: 'Academics' },
  { path: 'page/hospital', icon: Building2, label: 'Hospital' },
  { path: 'page/admissions', icon: ClipboardList, label: 'Admissions' },
  { path: 'page/campus_life', icon: Tent, label: 'Campus Life' },
  { path: 'page/faculty', icon: Users, label: 'Faculty' },
  { path: 'teaching-staff', icon: Users, label: 'Teaching Staff', dynamicCountKey: 'teachingStaff' },
  { path: 'page/nmc_disclosures', icon: FileText, label: 'NMC Disclosures' },
  { path: 'nmc-mandatory', icon: FileText, label: 'NMC Mandatory Info' },
  { path: 'page/contact', icon: Phone, label: 'Contact' },
  { path: 'uploads', icon: Upload, label: 'File Manager' },
  { path: 'enquiries', icon: ClipboardList, label: 'Enquiries' },
  { path: 'applications', icon: Briefcase, label: 'Job Applications' },
  { path: 'careers-config', icon: Settings, label: 'Careers Config' },
];

function Sidebar({ onLogout }) {
  const [counts, setCounts] = useState({ teachingStaff: null });

  useEffect(() => {
    let cancelled = false;
    const loadCounts = () => {
      fetch(`${API}/api/cms/teaching-staff`)
        .then(r => r.json())
        .then(d => { if (!cancelled) setCounts(c => ({ ...c, teachingStaff: (d.staff || []).length })); })
        .catch(() => {});
    };
    loadCounts();
    // Refresh whenever the CMS notifies of a staff change (fired by TeachingStaffManager on save)
    const handler = () => loadCounts();
    window.addEventListener('cms:teaching-staff-updated', handler);
    return () => { cancelled = true; window.removeEventListener('cms:teaching-staff-updated', handler); };
  }, []);

  return (
    <aside className="w-64 bg-[#1A202C] h-screen flex flex-col fixed left-0 top-0 z-40" data-testid="cms-sidebar">
      <div className="p-5 border-b border-gray-700 flex-shrink-0">
        <h1 className="text-lg font-bold text-white flex items-center gap-2">
          <div className="w-8 h-8 bg-[#4ECDC4] rounded-lg flex items-center justify-center"><Settings className="text-white" size={16} /></div>
          SSMCH CMS
        </h1>
      </div>
      <nav className="flex-1 min-h-0 p-3 overflow-y-auto cms-sidebar-scroll" data-testid="cms-sidebar-nav">
        {NAV_ITEMS.map(item => {
          const count = item.dynamicCountKey ? counts[item.dynamicCountKey] : null;
          const label = count != null ? `${item.label} (${count})` : item.label;
          return (
            <NavLink
              key={item.path}
              to={`/admin/cms/${item.path}`}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm mb-0.5 transition-colors ${isActive ? 'bg-[#4ECDC4]/20 text-[#4ECDC4]' : 'text-gray-400 hover:text-white hover:bg-white/5'}`
              }
              data-testid={`cms-nav-${item.path.replace(/\//g, '-') || 'dashboard'}`}
            >
              <item.icon size={18} />{label}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-gray-700 space-y-2 flex-shrink-0">
        <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-colors">
          <Globe size={18} />View Website
        </a>
        <button onClick={onLogout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:text-red-400 hover:bg-red-400/5 transition-colors w-full" data-testid="cms-logout-btn">
          <LogOut size={18} />Logout
        </button>
      </div>
    </aside>
  );
}

// ============== FIELD EDITOR COMPONENT ==============
function FieldEditor({ label, value, onChange, type = 'text', rows = 3, placeholder }) {
  if (type === 'textarea') {
    return (
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-gray-700">{label}</Label>
        <Textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder} className="text-sm" />
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-gray-700">{label}</Label>
      <Input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="text-sm" />
    </div>
  );
}

// ============== GALLERY PICKER ==============
function GalleryPicker({ onPick }) {
  const [open, setOpen] = React.useState(false);
  const [images, setImages] = React.useState([]);
  const [filter, setFilter] = React.useState('all');
  const [loading, setLoading] = React.useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/gallery`);
      const data = await res.json();
      setImages(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load gallery'); }
    setLoading(false);
  };

  React.useEffect(() => { if (open && images.length === 0) load(); }, [open]);  // eslint-disable-line react-hooks/exhaustive-deps

  const cats = ['all', 'campus', 'hostel', 'hospital', 'academics', 'events'];
  const visible = filter === 'all' ? images : images.filter(i => i.category === filter);

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)} className="text-xs h-9" data-testid="gallery-pick-btn">
        <Image size={12} className="mr-1" />Pick from Gallery
      </Button>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b flex items-center justify-between">
              <div>
                <h3 className="font-heading text-lg font-bold text-[#1A202C]">Pick from Gallery</h3>
                <p className="text-xs text-gray-500">{images.length} images available · click any to use</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Close</Button>
            </div>
            <div className="px-5 py-3 border-b flex gap-2 overflow-x-auto">
              {cats.map(c => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  className={`px-3 py-1 rounded-full text-xs whitespace-nowrap transition-colors ${filter === c ? 'bg-[#4ECDC4] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  data-testid={`picker-filter-${c}`}
                >
                  {c.charAt(0).toUpperCase() + c.slice(1)}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {loading ? (
                <p className="text-center py-12 text-gray-400 animate-pulse">Loading gallery...</p>
              ) : visible.length === 0 ? (
                <div className="text-center py-12 text-gray-400 text-sm">
                  No images in <strong>{filter}</strong>. Add some via <code className="bg-gray-100 px-1 rounded">/admin/cms/gallery</code>.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {visible.map(img => (
                    <button
                      key={img.id}
                      onClick={() => { onPick(normalizeImageUrl(img.image_url)); setOpen(false); toast.success(`Picked: ${img.title}`); }}
                      className="group relative aspect-square rounded-lg overflow-hidden border border-gray-200 hover:border-[#4ECDC4] transition-all"
                      data-testid={`picker-img-${img.id}`}
                    >
                      <img src={normalizeImageUrl(img.image_url)} alt={img.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                        <p className="text-white text-xs truncate text-left">{img.title}</p>
                        <p className="text-white/70 text-[10px] capitalize text-left">{img.category}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============== IMAGE URL INPUT (with Google Drive auto-convert + live preview) ==============
function ImageUrlInput({ label, value, onChange, placeholder = 'Paste image URL or Google Drive share link' }) {
  const [draft, setDraft] = React.useState(value || '');
  const [previewError, setPreviewError] = React.useState(false);

  React.useEffect(() => { setDraft(value || ''); setPreviewError(false); }, [value]);

  const commit = (raw) => {
    const normalized = normalizeImageUrl(raw);
    if (normalized !== raw && isGoogleDriveUrl(raw)) {
      toast.success('Converted Google Drive link → direct image URL');
    }
    setDraft(normalized);
    onChange(normalized);
  };

  const handleChange = (e) => { setDraft(e.target.value); setPreviewError(false); };
  const handleBlur = () => { if (draft !== value) commit(draft); };
  const handlePaste = (e) => {
    const pasted = (e.clipboardData || window.clipboardData).getData('text');
    if (isGoogleDriveUrl(pasted)) {
      e.preventDefault();
      commit(pasted);
    }
  };

  const converted = normalizeImageUrl(draft);
  const showPreview = converted && !previewError;
  const isDrive = isGoogleDriveUrl(draft);

  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-gray-700">{label}</Label>
      <div className="flex gap-2 items-start">
        <Input
          type="url"
          value={draft}
          onChange={handleChange}
          onBlur={handleBlur}
          onPaste={handlePaste}
          placeholder={placeholder}
          className="text-sm flex-1"
          data-testid="image-url-input"
        />
        <GalleryPicker onPick={(url) => commit(url)} />
      </div>
      <p className="text-xs text-gray-400">
        Paste any public image URL or a Google Drive share link (the LH3 link will be generated automatically).
        {isDrive && converted !== draft && (
          <span className="text-[#4ECDC4] ml-1">↪ will save as {converted.slice(0, 60)}…</span>
        )}
      </p>
      {showPreview && (
        <div className="mt-2 relative inline-block">
          <img
            src={converted}
            alt="Preview"
            onError={() => setPreviewError(true)}
            className="h-28 w-auto rounded-lg border border-gray-200 bg-gray-50 object-cover"
            data-testid="image-url-preview"
          />
        </div>
      )}
      {previewError && draft && (
        <p className="text-xs text-red-500">Preview failed — make sure the Drive file is set to "Anyone with the link can view".</p>
      )}
    </div>
  );
}

// ============== LIST EDITOR ==============
function ListEditor({ label, items, onChange, placeholder = 'Add item...' }) {
  const [newItem, setNewItem] = useState('');
  const add = () => { if (newItem.trim()) { onChange([...items, newItem.trim()]); setNewItem(''); } };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, val) => { const copy = [...items]; copy[i] = val; onChange(copy); };
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-gray-700">{label}</Label>
      {items.map((item, i) => (
        <div key={i} className="flex gap-2">
          <Input value={item} onChange={e => update(i, e.target.value)} className="text-sm flex-1" />
          <Button variant="ghost" size="sm" onClick={() => remove(i)} className="text-red-500 hover:text-red-700 shrink-0">Remove</Button>
        </div>
      ))}
      <div className="flex gap-2">
        <Input value={newItem} onChange={e => setNewItem(e.target.value)} placeholder={placeholder} className="text-sm flex-1" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())} />
        <Button variant="outline" size="sm" onClick={add} className="shrink-0">Add</Button>
      </div>
    </div>
  );
}

// ============== DASHBOARD ==============
function DashboardView() {
  const { get } = useApi();
  const [stats, setStats] = useState(null);
  useEffect(() => { get('/api/cms/stats').then(setStats).catch(() => {}); }, [get]);
  const cards = stats ? [
    { label: 'Settings Configured', value: stats.settings_configured ? 'Yes' : 'No', color: stats.settings_configured ? '#4ECDC4' : '#E53E3E' },
    { label: 'Pages Configured', value: stats.pages_configured, color: '#4ECDC4' },
    { label: 'Teaching Staff', value: stats.teaching_staff_count ?? 0, color: '#4ECDC4' },
    { label: 'File Uploads', value: stats.total_uploads, color: '#D4A84B' },
    { label: 'News Articles', value: stats.total_news, color: '#9ACD32' },
    { label: 'Gallery Images', value: stats.total_gallery, color: '#4ECDC4' },
    { label: 'Total Enquiries', value: stats.total_enquiries, color: '#D4A84B' },
    { label: 'New Enquiries', value: stats.new_enquiries, color: '#E53E3E' },
    { label: 'Total Applications', value: stats.total_applications ?? 0, color: '#D4A84B' },
    { label: 'New Applications', value: stats.new_applications ?? 0, color: '#E53E3E' },
  ] : [];
  return (
    <div data-testid="cms-dashboard">
      <h2 className="text-2xl font-bold text-[#1A202C] mb-6">Dashboard</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {cards.map((c, i) => (
          <Card key={i} className="border-0 shadow-sm"><CardContent className="p-5">
            <p className="text-sm text-gray-500 mb-1">{c.label}</p>
            <p className="text-2xl font-bold" style={{ color: c.color }}>{String(c.value)}</p>
          </CardContent></Card>
        ))}
      </div>
      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle className="text-lg">Quick Actions</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <NavLink to="/admin/cms/settings"><Button variant="outline" className="w-full justify-start gap-2"><Settings size={16} />Edit Global Settings</Button></NavLink>
          <NavLink to="/admin/cms/uploads"><Button variant="outline" className="w-full justify-start gap-2"><Upload size={16} />Upload Files</Button></NavLink>
          <NavLink to="/admin/cms/enquiries"><Button variant="outline" className="w-full justify-start gap-2"><ClipboardList size={16} />View Enquiries</Button></NavLink>
          <NavLink to="/admin/cms/applications"><Button variant="outline" className="w-full justify-start gap-2"><Briefcase size={16} />View Applications</Button></NavLink>
        </CardContent>
      </Card>
    </div>
  );
}

// ============== GLOBAL SETTINGS EDITOR ==============
function SettingsEditor() {
  const { get, put } = useApi();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { get('/api/cms/settings').then(setData).catch(() => toast.error('Failed to load settings')); }, [get]);

  const save = async () => {
    setSaving(true);
    try { await put('/api/cms/settings', data); toast.success('Settings saved!'); }
    catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const set = (path, value) => {
    setData(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = copy;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return copy;
    });
  };

  if (!data) return <div className="animate-pulse text-gray-400">Loading settings...</div>;

  return (
    <div data-testid="cms-settings-editor">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-[#1A202C]">Global Settings</h2>
        <Button onClick={save} disabled={saving} className="bg-[#4ECDC4] hover:bg-[#3DBDB4] gap-2" data-testid="cms-save-settings">
          <Save size={16} />{saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
      <p className="text-sm text-gray-500 mb-6 bg-blue-50 p-3 rounded-lg">These settings are shared across the entire website. Changes here will update all pages that use this data.</p>

      <div className="space-y-8">
        {/* Basic Info */}
        <Card className="border-0 shadow-sm"><CardHeader><CardTitle>Basic Information</CardTitle></CardHeader><CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldEditor label="College Name" value={data.college_name} onChange={v => set('college_name', v)} />
          <FieldEditor label="Short Name" value={data.short_name} onChange={v => set('short_name', v)} />
          <FieldEditor label="Tagline" value={data.tagline} onChange={v => set('tagline', v)} className="col-span-2" />
        </CardContent></Card>

        {/* Contact */}
        <Card className="border-0 shadow-sm"><CardHeader><CardTitle>Contact Information</CardTitle></CardHeader><CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldEditor label="Phone" value={data.contact?.phone} onChange={v => set('contact.phone', v)} />
          <FieldEditor label="Admissions Email" value={data.contact?.emails?.admissions} onChange={v => set('contact.emails.admissions', v)} />
          <FieldEditor label="Info Email" value={data.contact?.emails?.info} onChange={v => set('contact.emails.info', v)} />
          <FieldEditor label="Academics Email" value={data.contact?.emails?.academics} onChange={v => set('contact.emails.academics', v)} />
          <FieldEditor label="Hospital Email" value={data.contact?.emails?.hospital} onChange={v => set('contact.emails.hospital', v)} />
          <FieldEditor label="Hostel Email" value={data.contact?.emails?.hostel} onChange={v => set('contact.emails.hostel', v)} />
        </CardContent></Card>

        {/* Address */}
        <Card className="border-0 shadow-sm"><CardHeader><CardTitle>Address</CardTitle></CardHeader><CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldEditor label="Address Line 1" value={data.address?.line1} onChange={v => set('address.line1', v)} />
          <FieldEditor label="Address Line 2" value={data.address?.line2} onChange={v => set('address.line2', v)} />
          <FieldEditor label="City" value={data.address?.city} onChange={v => set('address.city', v)} />
          <FieldEditor label="State" value={data.address?.state} onChange={v => set('address.state', v)} />
          <FieldEditor label="Pincode" value={data.address?.pincode} onChange={v => set('address.pincode', v)} />
          <FieldEditor label="Full Address" value={data.address?.full} onChange={v => set('address.full', v)} type="textarea" rows={2} />
        </CardContent></Card>

        {/* Google Maps */}
        <Card className="border-0 shadow-sm"><CardHeader><CardTitle>Google Maps</CardTitle></CardHeader><CardContent className="space-y-4">
          <FieldEditor label="Google Maps URL" value={data.google_maps?.url} onChange={v => set('google_maps.url', v)} placeholder="https://www.google.com/maps/place/..." />
          <FieldEditor label="Google Maps Embed URL" value={data.google_maps?.embed_url} onChange={v => set('google_maps.embed_url', v)} type="textarea" rows={2} placeholder="https://www.google.com/maps/embed?pb=..." />
        </CardContent></Card>

        {/* Social Links */}
        <Card className="border-0 shadow-sm"><CardHeader><CardTitle>Social Media Links</CardTitle></CardHeader><CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FieldEditor label="Facebook URL" value={data.social_links?.facebook} onChange={v => set('social_links.facebook', v)} />
          <FieldEditor label="Instagram URL" value={data.social_links?.instagram} onChange={v => set('social_links.instagram', v)} />
          <FieldEditor label="YouTube URL" value={data.social_links?.youtube} onChange={v => set('social_links.youtube', v)} />
        </CardContent></Card>

        {/* Dean */}
        <Card className="border-0 shadow-sm"><CardHeader><CardTitle>Dean Information</CardTitle></CardHeader><CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FieldEditor label="Dean Name" value={data.dean?.name} onChange={v => set('dean.name', v)} />
            <FieldEditor label="Designation" value={data.dean?.designation} onChange={v => set('dean.designation', v)} />
          </div>
          <ImageUrlInput label="Photo URL" value={data.dean?.photo_url} onChange={v => set('dean.photo_url', v)} />
          <FieldEditor label="Full Message (About page)" value={data.dean?.message} onChange={v => set('dean.message', v)} type="textarea" rows={6} />
          <FieldEditor label="Short Quote (Home page)" value={data.dean?.short_quote} onChange={v => set('dean.short_quote', v)} type="textarea" rows={3} />
        </CardContent></Card>

        {/* Office Hours */}
        <Card className="border-0 shadow-sm"><CardHeader><CardTitle>Office Hours</CardTitle></CardHeader><CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FieldEditor label="Days" value={data.office_hours?.days} onChange={v => set('office_hours.days', v)} />
          <FieldEditor label="Time" value={data.office_hours?.time} onChange={v => set('office_hours.time', v)} />
          <FieldEditor label="Note" value={data.office_hours?.note} onChange={v => set('office_hours.note', v)} />
        </CardContent></Card>

        {/* Trust Indicators */}
        <Card className="border-0 shadow-sm"><CardHeader><CardTitle>Trust Indicators (Home Page)</CardTitle></CardHeader><CardContent className="space-y-4">
          {data.trust_indicators?.map((item, i) => (
            <div key={i} className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-lg">
              <FieldEditor label="Value" value={item.value} onChange={v => { const copy = [...data.trust_indicators]; copy[i] = { ...copy[i], value: v }; set('trust_indicators', copy); }} />
              <FieldEditor label="Label" value={item.label} onChange={v => { const copy = [...data.trust_indicators]; copy[i] = { ...copy[i], label: v }; set('trust_indicators', copy); }} />
              <FieldEditor label="Subtext" value={item.subtext} onChange={v => { const copy = [...data.trust_indicators]; copy[i] = { ...copy[i], subtext: v }; set('trust_indicators', copy); }} />
              <FieldEditor label="Extra Detail" value={item.details || item.extra || ''} onChange={v => { const copy = [...data.trust_indicators]; copy[i] = { ...copy[i], details: v }; set('trust_indicators', copy); }} />
            </div>
          ))}
        </CardContent></Card>
      </div>
    </div>
  );
}

// ============== PAGE EDITOR ==============
function PageEditor({ pageKey, title }) {
  const { get, put } = useApi();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [raw, setRaw] = useState('');
  const [mode, setMode] = useState('form');

  useEffect(() => {
    get(`/api/cms/pages/${pageKey}`).then(d => { setData(d); setRaw(JSON.stringify(d, null, 2)); }).catch(() => toast.error('Failed to load page'));
  }, [get, pageKey]);

  const save = async () => {
    setSaving(true);
    try {
      const toSave = mode === 'json' ? JSON.parse(raw) : data;
      await put(`/api/cms/pages/${pageKey}`, toSave);
      setData(toSave);
      setRaw(JSON.stringify(toSave, null, 2));
      toast.success('Page saved!');
    } catch (e) {
      toast.error(mode === 'json' ? 'Invalid JSON or save failed' : 'Failed to save');
    }
    setSaving(false);
  };

  const set = (path, value) => {
    setData(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = copy;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) obj[keys[i]] = {};
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      setRaw(JSON.stringify(copy, null, 2));
      return copy;
    });
  };

  if (!data) return <div className="animate-pulse text-gray-400">Loading...</div>;

  return (
    <div data-testid={`cms-page-${pageKey}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-[#1A202C]">{title}</h2>
        <div className="flex gap-2">
          <Button variant={mode === 'form' ? 'default' : 'outline'} size="sm" onClick={() => setMode('form')} className={mode === 'form' ? 'bg-[#4ECDC4]' : ''}>Form View</Button>
          <Button variant={mode === 'json' ? 'default' : 'outline'} size="sm" onClick={() => { setRaw(JSON.stringify(data, null, 2)); setMode('json'); }} className={mode === 'json' ? 'bg-[#4ECDC4]' : ''}>JSON View</Button>
          <a href={`/${pageKey === 'home' ? '' : pageKey.replace('_', '-')}`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm" className="gap-1"><Eye size={14} />Preview</Button>
          </a>
          <Button onClick={save} disabled={saving} className="bg-[#4ECDC4] hover:bg-[#3DBDB4] gap-2" data-testid={`cms-save-${pageKey}`}>
            <Save size={16} />{saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {mode === 'json' ? (
        <Textarea value={raw} onChange={e => setRaw(e.target.value)} rows={30} className="font-mono text-xs" />
      ) : (
        <div className="space-y-6">
          {/* Hero Section - common to most pages */}
          {data.hero && (
            <Card className="border-0 shadow-sm"><CardHeader><CardTitle>Hero Section</CardTitle></CardHeader><CardContent className="space-y-4">
              <FieldEditor label="Hero Image URL" value={data.hero.image} onChange={v => set('hero.image', v)} />
              {data.hero.image && <img src={data.hero.image} alt="Hero" className="w-full h-40 object-cover rounded-lg border" />}
              <FieldEditor label="Badge Text" value={data.hero.badge} onChange={v => set('hero.badge', v)} />
              <FieldEditor label="Title" value={data.hero.title} onChange={v => set('hero.title', v)} />
              <FieldEditor label="Subtitle" value={data.hero.subtitle} onChange={v => set('hero.subtitle', v)} type="textarea" />
            </CardContent></Card>
          )}

          {/* Hero image only (for simpler pages) */}
          {data.hero_image !== undefined && !data.hero && (
            <Card className="border-0 shadow-sm"><CardHeader><CardTitle>Hero Image</CardTitle></CardHeader><CardContent>
              <ImageUrlInput label="Hero Image" value={data.hero_image} onChange={v => set('hero_image', v)} />
            </CardContent></Card>
          )}

          {/* Dynamic sections based on page data */}
          {Object.entries(data).filter(([k]) => !['hero', 'hero_image', 'page_key', 'updated_at', 'updated_by', 'created_at'].includes(k)).map(([key, value]) => (
            <Card key={key} className="border-0 shadow-sm">
              <CardHeader><CardTitle className="capitalize">{key.replace(/_/g, ' ')}</CardTitle></CardHeader>
              <CardContent>
                {typeof value === 'string' ? (
                  isImageField(key) ? (
                    <ImageUrlInput label="" value={value} onChange={v => set(key, v)} />
                  ) : (
                    <FieldEditor label="" value={value} onChange={v => set(key, v)} type="textarea" />
                  )
                ) : Array.isArray(value) ? (
                  typeof value[0] === 'string' ? (
                    <ListEditor label="" items={value} onChange={v => set(key, v)} />
                  ) : (
                    <div className="space-y-3">
                      {value.map((item, i) => (
                        <div key={i} className="p-4 bg-gray-50 rounded-lg space-y-3">
                          <p className="text-xs font-semibold text-gray-400 uppercase">Item {i + 1}</p>
                          {Object.entries(item).filter(([k]) => k !== 'icon' && k !== 'highlight').map(([k, v]) => (
                            typeof v === 'string' || typeof v === 'number' ? (
                              isImageField(k) ? (
                                <ImageUrlInput key={k} label={k.replace(/_/g, ' ')} value={String(v)} onChange={val => {
                                  const copy = JSON.parse(JSON.stringify(value));
                                  copy[i][k] = val;
                                  set(key, copy);
                                }} />
                              ) : (
                                <FieldEditor key={k} label={k.replace(/_/g, ' ')} value={String(v)} onChange={val => {
                                  const copy = JSON.parse(JSON.stringify(value));
                                  copy[i][k] = val;
                                  set(key, copy);
                                }} type={String(v).length > 80 ? 'textarea' : 'text'} />
                              )
                            ) : Array.isArray(v) && typeof v[0] === 'string' ? (
                              <ListEditor key={k} label={k.replace(/_/g, ' ')} items={v} onChange={val => {
                                const copy = JSON.parse(JSON.stringify(value));
                                copy[i][k] = val;
                                set(key, copy);
                              }} />
                            ) : null
                          ))}
                        </div>
                      ))}
                    </div>
                  )
                ) : typeof value === 'object' && value !== null ? (
                  <div className="space-y-3">
                    {Object.entries(value).map(([k, v]) => (
                      typeof v === 'string' || typeof v === 'number' ? (
                        isImageField(k) ? (
                          <ImageUrlInput key={k} label={k.replace(/_/g, ' ')} value={String(v)} onChange={val => set(`${key}.${k}`, val)} />
                        ) : (
                          <FieldEditor key={k} label={k.replace(/_/g, ' ')} value={String(v)} onChange={val => set(`${key}.${k}`, val)} type={String(v).length > 80 ? 'textarea' : 'text'} />
                        )
                      ) : Array.isArray(v) && typeof v[0] === 'string' ? (
                        <ListEditor key={k} label={k.replace(/_/g, ' ')} items={v} onChange={val => set(`${key}.${k}`, val)} />
                      ) : null
                    ))}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ============== FILE MANAGER ==============
function FileManager() {
  const { get, upload } = useApi();
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState('general');

  const loadUploads = useCallback(() => { get('/api/cms/uploads').then(setUploads).catch(() => {}); }, [get]);
  useEffect(() => { loadUploads(); }, [loadUploads]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    setUploading(true);
    for (const file of files) {
      try {
        await upload(file, category, '');
        toast.success(`Uploaded ${file.name}`);
      } catch { toast.error(`Failed: ${file.name}`); }
    }
    setUploading(false);
    loadUploads();
    e.target.value = '';
  };

  const copyUrl = (u) => {
    const url = `${API}/api/cms/files/${u.storage_path}`;
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard!');
  };

  return (
    <div data-testid="cms-file-manager">
      <h2 className="text-2xl font-bold text-[#1A202C] mb-6">File Manager</h2>
      <Card className="border-0 shadow-sm mb-6">
        <CardContent className="p-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <Label className="text-sm">Category</Label>
              <select value={category} onChange={e => setCategory(e.target.value)} className="block w-48 border rounded-md px-3 py-2 text-sm mt-1">
                <option value="general">General</option>
                <option value="campus">Campus Photos</option>
                <option value="faculty">Faculty Photos</option>
                <option value="hospital">Hospital</option>
                <option value="documents">Documents</option>
                <option value="nmc">NMC Disclosures</option>
                <option value="gallery">Gallery</option>
              </select>
            </div>
            <div>
              <Label className="text-sm">Upload Files</Label>
              <Input type="file" multiple onChange={handleUpload} disabled={uploading} className="mt-1" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" data-testid="cms-file-input" />
            </div>
            {uploading && <p className="text-sm text-[#4ECDC4] animate-pulse">Uploading...</p>}
          </div>
        </CardContent>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {uploads.map(u => (
          <Card key={u.id} className="border-0 shadow-sm overflow-hidden">
            {u.content_type?.startsWith('image/') ? (
              <img src={`${API}/api/cms/files/${u.storage_path}`} alt={u.original_filename} className="w-full h-40 object-cover" />
            ) : (
              <div className="w-full h-40 bg-gray-100 flex items-center justify-center"><FileText size={48} className="text-gray-300" /></div>
            )}
            <CardContent className="p-4">
              <p className="text-sm font-medium truncate" title={u.original_filename}>{u.original_filename}</p>
              <p className="text-xs text-gray-400">{u.category} &middot; {(u.size / 1024).toFixed(0)} KB</p>
              <Button variant="outline" size="sm" onClick={() => copyUrl(u)} className="mt-2 w-full text-xs">Copy URL</Button>
            </CardContent>
          </Card>
        ))}
      </div>
      {uploads.length === 0 && <p className="text-center text-gray-400 py-12">No files uploaded yet</p>}
    </div>
  );
}

// ============== NEWS MANAGER ==============
function NewsManager() {
  const { headers } = useAuth();
  const [news, setNews] = useState([]);
  const [form, setForm] = useState({ title: '', content: '', category: 'announcement', image_url: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/news?include_unpublished=true`, { headers });
      setNews(await res.json());
    } catch { toast.error('Failed to load news'); }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/news`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success('Article created');
      setForm({ title: '', content: '', category: 'announcement', image_url: '' });
      load();
    } catch { toast.error('Failed to create article'); }
    setSaving(false);
  };

  const startEdit = (n) => {
    setEditingId(n.id);
    setEditForm({ title: n.title, content: n.content, category: n.category, image_url: n.image_url || '', is_published: n.is_published });
  };
  const cancelEdit = () => { setEditingId(null); setEditForm({}); };
  const saveEdit = async () => {
    try {
      const res = await fetch(`${API}/api/news/${editingId}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      if (!res.ok) throw new Error();
      toast.success('Article updated');
      cancelEdit();
      load();
    } catch { toast.error('Update failed'); }
  };

  const togglePublish = async (n) => {
    try {
      await fetch(`${API}/api/news/${n.id}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_published: !n.is_published }),
      });
      load();
    } catch { toast.error('Update failed'); }
  };

  const deleteNews = async (id) => {
    if (!window.confirm('Delete this article permanently?')) return;
    await fetch(`${API}/api/news/${id}`, { method: 'DELETE', headers });
    toast.success('Deleted');
    load();
  };

  return (
    <div data-testid="cms-news-manager">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#1A202C]">News Articles</h2>
        <p className="text-sm text-gray-500 mt-1">{news.length} total · {news.filter(n => n.is_published).length} published · drafts appear here too</p>
      </div>
      <Card className="border-0 shadow-sm mb-6"><CardHeader><CardTitle>Add Article</CardTitle></CardHeader><CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldEditor label="Title" value={form.title} onChange={v => setForm(p => ({ ...p, title: v }))} />
          <div className="space-y-1.5"><Label className="text-sm">Category</Label>
            <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="w-full border rounded-md px-3 py-2 text-sm bg-white">
              <option value="announcement">Announcement</option>
              <option value="event">Event</option>
              <option value="notice">Notice</option>
            </select>
          </div>
        </div>
        <FieldEditor label="Content" value={form.content} onChange={v => setForm(p => ({ ...p, content: v }))} type="textarea" rows={4} />
        <ImageUrlInput label="Image URL (optional)" value={form.image_url} onChange={v => setForm(p => ({ ...p, image_url: v }))} />
        <Button onClick={save} disabled={saving} className="bg-[#4ECDC4] hover:bg-[#3DBDB4]" data-testid="news-create-btn">
          {saving ? 'Publishing...' : 'Publish Article'}
        </Button>
      </CardContent></Card>

      <div className="space-y-3">
        {news.length === 0 && <p className="text-center text-gray-400 py-8">No articles yet — publish your first above.</p>}
        {news.map(n => (
          <Card key={n.id} className="border-0 shadow-sm" data-testid={`news-row-${n.id}`}>
            {editingId === n.id ? (
              <CardContent className="p-5 space-y-3 bg-[#4ECDC4]/5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input value={editForm.title} onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))} placeholder="Title" className="text-sm" />
                  <select value={editForm.category} onChange={e => setEditForm(p => ({ ...p, category: e.target.value }))} className="border rounded-md px-3 text-sm bg-white">
                    <option value="announcement">Announcement</option>
                    <option value="event">Event</option>
                    <option value="notice">Notice</option>
                  </select>
                </div>
                <Textarea rows={3} value={editForm.content} onChange={e => setEditForm(p => ({ ...p, content: e.target.value }))} className="text-sm" />
                <ImageUrlInput label="Image URL" value={editForm.image_url} onChange={v => setEditForm(p => ({ ...p, image_url: v }))} />
                <label className="flex items-center gap-2 text-xs text-gray-700">
                  <input type="checkbox" checked={editForm.is_published} onChange={e => setEditForm(p => ({ ...p, is_published: e.target.checked }))} />
                  Published (visible on public site)
                </label>
                <div className="flex gap-2 pt-1">
                  <Button size="sm" onClick={saveEdit} className="bg-[#4ECDC4] hover:bg-[#3DBDB4] text-white text-xs">Save</Button>
                  <Button size="sm" variant="ghost" onClick={cancelEdit} className="text-xs">Cancel</Button>
                </div>
              </CardContent>
            ) : (
              <CardContent className="p-4 flex items-start justify-between gap-4">
                <div className="flex gap-3 flex-1 min-w-0">
                  {n.image_url && (
                    <img src={normalizeImageUrl(n.image_url)} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0 border border-gray-100" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-[#1A202C]">{n.title}</p>
                      <span className={`text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded ${n.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                        {n.is_published ? 'Published' : 'Draft'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">{n.category} · {new Date(n.created_at).toLocaleDateString()}</p>
                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">{n.content}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(n)} className="text-[#4ECDC4] text-xs h-7" data-testid={`news-edit-${n.id}`}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => togglePublish(n)} className="text-gray-600 text-xs h-7">{n.is_published ? 'Unpublish' : 'Publish'}</Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteNews(n.id)} className="text-red-500 text-xs h-7">Delete</Button>
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

// ============== GALLERY MANAGER ==============
function GalleryManager() {
  const { get } = useApi();
  const { headers } = useAuth();
  const [images, setImages] = useState([]);
  const [form, setForm] = useState({ title: '', image_url: '', category: 'campus', description: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => { get('/api/gallery').then(setImages).catch(() => {}); }, [get]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/gallery`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      toast.success('Image added!');
      setForm({ title: '', image_url: '', category: 'campus', description: '' });
      load();
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  const deleteImg = async (id) => {
    if (!window.confirm('Delete this image permanently?')) return;
    await fetch(`${API}/api/gallery/${id}`, { method: 'DELETE', headers });
    toast.success('Deleted');
    load();
  };

  const updateImg = async (id, patch) => {
    try {
      const res = await fetch(`${API}/api/gallery/${id}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error();
      toast.success('Updated');
      load();
      return true;
    } catch { toast.error('Update failed'); return false; }
  };

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const startEdit = (img) => {
    setEditingId(img.id);
    setEditForm({ title: img.title || '', category: img.category || 'campus', description: img.description || '', image_url: img.image_url || '' });
  };
  const cancelEdit = () => { setEditingId(null); setEditForm({}); };
  const saveEdit = async () => {
    if (await updateImg(editingId, editForm)) cancelEdit();
  };

  return (
    <div data-testid="cms-gallery-manager">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#1A202C]">Campus Gallery</h2>
          <p className="text-sm text-gray-500 mt-1">Add photos here once — they appear on the public Gallery page and become reusable across all CMS pages via the "Pick from Gallery" button.</p>
        </div>
      </div>
      <Card className="border-0 shadow-sm mb-6"><CardHeader><CardTitle>Add Image</CardTitle></CardHeader><CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldEditor label="Title" value={form.title} onChange={v => setForm(p => ({ ...p, title: v }))} />
          <div className="space-y-1.5"><Label className="text-sm">Category</Label>
            <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="w-full border rounded-md px-3 py-2 text-sm">
              <option value="campus">Campus</option><option value="hospital">Hospital</option><option value="academics">Academics</option><option value="events">Events</option><option value="hostel">Hostel</option>
            </select>
          </div>
        </div>
        <ImageUrlInput label="Image URL" value={form.image_url} onChange={v => setForm(p => ({ ...p, image_url: v }))} placeholder="Paste image URL or Google Drive share link" />
        <FieldEditor label="Description" value={form.description} onChange={v => setForm(p => ({ ...p, description: v }))} type="textarea" rows={2} />
        <Button onClick={save} disabled={saving} className="bg-[#4ECDC4] hover:bg-[#3DBDB4]">{saving ? 'Adding...' : 'Add Image'}</Button>
      </CardContent></Card>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map(img => (
          <Card key={img.id} className="border-0 shadow-sm overflow-hidden flex flex-col" data-testid={`gallery-card-${img.id}`}>
            <img src={normalizeImageUrl(img.image_url)} alt={img.title} className="w-full h-32 object-cover" />
            {editingId === img.id ? (
              <CardContent className="p-3 space-y-2 flex-1 bg-[#4ECDC4]/5">
                <Input value={editForm.title} onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))} placeholder="Title" className="text-xs h-8" data-testid={`edit-title-${img.id}`} />
                <select value={editForm.category} onChange={e => setEditForm(p => ({ ...p, category: e.target.value }))} className="w-full border rounded-md px-2 py-1 text-xs bg-white" data-testid={`edit-category-${img.id}`}>
                  <option value="campus">Campus</option>
                  <option value="hostel">Hostel</option>
                  <option value="hospital">Hospital</option>
                  <option value="academics">Academics</option>
                  <option value="events">Events</option>
                </select>
                <Textarea rows={2} value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} placeholder="Description" className="text-xs" data-testid={`edit-description-${img.id}`} />
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveEdit} className="bg-[#4ECDC4] hover:bg-[#3DBDB4] text-white text-xs h-7 flex-1" data-testid={`edit-save-${img.id}`}>Save</Button>
                  <Button size="sm" variant="ghost" onClick={cancelEdit} className="text-xs h-7">Cancel</Button>
                </div>
              </CardContent>
            ) : (
              <CardContent className="p-3 flex-1 flex flex-col">
                <p className="text-sm font-medium truncate">{img.title}</p>
                <p className="text-xs text-gray-400 capitalize">{img.category}</p>
                {img.description && <p className="text-[11px] text-gray-500 line-clamp-2 mt-1">{img.description}</p>}
                <div className="flex gap-1 mt-2 pt-2 border-t border-gray-100">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(img)} className="text-[#4ECDC4] flex-1 text-xs h-7" data-testid={`edit-btn-${img.id}`}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteImg(img.id)} className="text-red-500 flex-1 text-xs h-7">Delete</Button>
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

// ============== TEACHING STAFF MANAGER ==============
function TeachingStaffManager() {
  const { headers } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('All');
  const [confirmReset, setConfirmReset] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/cms/teaching-staff`);
      const data = await res.json();
      setStaff(data.staff || []);
    } catch { toast.error('Failed to load teaching staff'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/cms/teaching-staff`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ staff }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setStaff(data.staff);
      toast.success(`Saved ${data.total} staff records`);
      window.dispatchEvent(new Event('cms:teaching-staff-updated'));
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const resetToOfficial = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/cms/teaching-staff/seed`, { method: 'POST', headers });
      if (!res.ok) throw new Error();
      toast.success('Reset to official 296-member list');
      setConfirmReset(false);
      load();
      window.dispatchEvent(new Event('cms:teaching-staff-updated'));
    } catch { toast.error('Reset failed'); }
    setSaving(false);
  };

  const updateRow = (idx, field, value) => {
    setStaff(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  const removeRow = (idx) => {
    setStaff(prev => prev.filter((_, i) => i !== idx));
  };

  const addRow = () => {
    setStaff(prev => [...prev, {
      sno: prev.length + 1,
      name: '',
      registration: '',
      department: '',
      designation: '',
      qualification: '',
    }]);
  };

  const departments = ['All', ...Array.from(new Set(staff.map(s => s.department))).filter(Boolean).sort()];

  // Column visibility (persisted per admin browser)
  const COLUMN_DEFS = [
    { key: 'sno', label: '# / S.No' },
    { key: 'name', label: 'Name' },
    { key: 'registration', label: 'Registration' },
    { key: 'department', label: 'Department' },
    { key: 'designation', label: 'Designation' },
    { key: 'actions', label: 'Actions (Remove)' },
  ];
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const stored = localStorage.getItem('cms_teaching_staff_cols');
      if (stored) return { ...Object.fromEntries(COLUMN_DEFS.map(c => [c.key, true])), ...JSON.parse(stored) };
    } catch {}
    return Object.fromEntries(COLUMN_DEFS.map(c => [c.key, true]));
  });
  const [showColMenu, setShowColMenu] = useState(false);
  useEffect(() => {
    try { localStorage.setItem('cms_teaching_staff_cols', JSON.stringify(visibleCols)); } catch {}
  }, [visibleCols]);
  const toggleCol = (key) => setVisibleCols(prev => ({ ...prev, [key]: !prev[key] }));
  const hiddenCount = COLUMN_DEFS.filter(c => !visibleCols[c.key]).length;
  const visibleColCount = COLUMN_DEFS.filter(c => visibleCols[c.key]).length;

  // Apply filters but preserve original index for updates
  const visible = staff
    .map((row, origIdx) => ({ row, origIdx }))
    .filter(({ row }) => {
      if (deptFilter !== 'All' && row.department !== deptFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (row.name || '').toLowerCase().includes(q)
          || (row.registration || '').toLowerCase().includes(q)
          || (row.department || '').toLowerCase().includes(q);
      }
      return true;
    });

  if (loading) return <div className="animate-pulse text-gray-400">Loading teaching staff...</div>;

  return (
    <div data-testid="cms-teaching-staff">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-[#1A202C]">Teaching Staff</h2>
          <p className="text-sm text-gray-500 mt-1">{staff.length} members &middot; shown on <code className="text-xs bg-gray-100 px-1 rounded">/teaching-staff</code></p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input
            id="staff-csv-input"
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              try {
                const text = await file.text();
                // Simple CSV parser supporting quoted fields
                const parseLine = (line) => {
                  const out = [];
                  let cur = '', inQ = false;
                  for (let i = 0; i < line.length; i++) {
                    const ch = line[i];
                    if (inQ) {
                      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
                      else if (ch === '"') inQ = false;
                      else cur += ch;
                    } else {
                      if (ch === ',') { out.push(cur); cur = ''; }
                      else if (ch === '"') inQ = true;
                      else cur += ch;
                    }
                  }
                  out.push(cur);
                  return out.map(s => s.trim());
                };
                const lines = text.replace(/\r\n/g, '\n').split('\n').filter(l => l.trim());
                if (lines.length < 2) { toast.error('CSV has no data rows'); return; }
                const header = parseLine(lines[0]).map(h => h.toLowerCase());
                const idx = {
                  name: header.indexOf('name'),
                  registration: header.findIndex(h => h.startsWith('registration')),
                  department: header.indexOf('department'),
                  designation: header.indexOf('designation'),
                };
                if (idx.name < 0 || idx.department < 0 || idx.designation < 0) {
                  toast.error('CSV must have columns: name, registration, department, designation');
                  return;
                }
                const parsed = lines.slice(1).map((line, i) => {
                  const cols = parseLine(line);
                  return {
                    sno: i + 1,
                    name: cols[idx.name] || '',
                    registration: idx.registration >= 0 ? (cols[idx.registration] || '') : '',
                    department: cols[idx.department] || '',
                    designation: cols[idx.designation] || '',
                  };
                }).filter(r => r.name);
                if (!parsed.length) { toast.error('No valid rows found'); return; }
                if (!window.confirm(`Import ${parsed.length} staff rows? This will REPLACE the current list. Click Save Changes after to persist.`)) return;
                setStaff(parsed);
                toast.success(`Loaded ${parsed.length} rows — click Save Changes to persist`);
              } catch (err) {
                toast.error('Failed to parse CSV: ' + err.message);
              }
            }}
          />
          <Button variant="outline" size="sm" onClick={() => {
            const csv = 'name,registration,department,designation\n"Dr. Example Name","MP-12345","Anatomy","Assistant Professor"\n';
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = 'teaching-staff-template.csv'; a.click();
            URL.revokeObjectURL(url);
          }} data-testid="staff-download-template">
            <Download size={14} className="mr-1" />CSV Template
          </Button>
          <Button variant="outline" size="sm" onClick={() => document.getElementById('staff-csv-input').click()} className="text-[#4ECDC4] border-[#4ECDC4]/30" data-testid="staff-upload-csv">
            <Upload size={14} className="mr-1" />Upload CSV
          </Button>
          <Button variant="outline" size="sm" onClick={addRow} data-testid="staff-add-row">+ Add Row</Button>
          <Button variant="outline" size="sm" onClick={() => setConfirmReset(v => !v)} className="text-[#D4A84B] border-[#D4A84B]/30" data-testid="staff-reset-btn">
            <RefreshCw size={14} className="mr-1" />Reset to Official
          </Button>
          <Button onClick={save} disabled={saving} className="bg-[#4ECDC4] hover:bg-[#3DBDB4]" data-testid="staff-save-btn">
            <Save size={14} className="mr-1" />{saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {confirmReset && (
        <Card className="border-0 shadow-sm bg-[#D4A84B]/10 mb-4">
          <CardContent className="p-4 flex items-center justify-between gap-4">
            <p className="text-sm text-[#1A202C]">This will overwrite ALL current staff with the official 296-member list from ssmch.in. Continue?</p>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setConfirmReset(false)}>Cancel</Button>
              <Button size="sm" onClick={resetToOfficial} disabled={saving} className="bg-[#D4A84B] hover:bg-[#B88F35] text-white" data-testid="staff-confirm-reset">Yes, Reset</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-0 shadow-sm mb-4">
        <CardContent className="p-4 flex flex-col md:flex-row gap-3 md:items-center">
          <Input placeholder="Search name, registration, department..." value={search} onChange={e => setSearch(e.target.value)} className="flex-1" data-testid="staff-search-input" />
          <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} className="border rounded-md px-3 py-2 text-sm bg-white min-w-[200px]">
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <div className="relative">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowColMenu(v => !v)}
              className="whitespace-nowrap"
              data-testid="staff-columns-btn"
            >
              <Eye size={14} className="mr-1" />
              Columns {hiddenCount > 0 && <span className="ml-1 text-xs bg-[#D4A84B]/20 text-[#D4A84B] px-1.5 rounded">{hiddenCount} hidden</span>}
            </Button>
            {showColMenu && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowColMenu(false)} />
                <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-40 p-3" data-testid="staff-columns-menu">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Show / Hide Columns</p>
                    <button
                      type="button"
                      onClick={() => setVisibleCols(Object.fromEntries(COLUMN_DEFS.map(c => [c.key, true])))}
                      className="text-[11px] text-[#4ECDC4] hover:underline"
                      data-testid="staff-columns-reset"
                    >
                      Show all
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {COLUMN_DEFS.map(c => (
                      <label
                        key={c.key}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer text-sm"
                        data-testid={`staff-col-toggle-${c.key}`}
                      >
                        <input
                          type="checkbox"
                          checked={!!visibleCols[c.key]}
                          onChange={() => toggleCol(c.key)}
                          disabled={visibleColCount === 1 && visibleCols[c.key]}
                          className="accent-[#4ECDC4]"
                        />
                        <span className="text-gray-700">{c.label}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2">Only affects this CMS table — the public page is unchanged.</p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm overflow-hidden">
        <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#1A202C] text-white z-10">
              <tr>
                {visibleCols.sno && <th className="px-3 py-2 text-left font-medium w-12">#</th>}
                {visibleCols.name && <th className="px-3 py-2 text-left font-medium min-w-[180px]">Name</th>}
                {visibleCols.registration && <th className="px-3 py-2 text-left font-medium min-w-[140px]">Registration</th>}
                {visibleCols.department && <th className="px-3 py-2 text-left font-medium min-w-[150px]">Department</th>}
                {visibleCols.designation && <th className="px-3 py-2 text-left font-medium min-w-[150px]">Designation</th>}
                {visibleCols.actions && <th className="px-3 py-2 text-right font-medium w-16"></th>}
              </tr>
            </thead>
            <tbody>
              {visible.map(({ row, origIdx }) => (
                <tr key={origIdx} className="border-b border-gray-100 hover:bg-gray-50">
                  {visibleCols.sno && <td className="px-3 py-1.5 text-gray-400 text-xs">{row.sno}</td>}
                  {visibleCols.name && <td className="px-2 py-1"><Input value={row.name || ''} onChange={e => updateRow(origIdx, 'name', e.target.value)} className="text-xs h-8" /></td>}
                  {visibleCols.registration && <td className="px-2 py-1"><Input value={row.registration || ''} onChange={e => updateRow(origIdx, 'registration', e.target.value)} className="text-xs h-8" /></td>}
                  {visibleCols.department && <td className="px-2 py-1"><Input value={row.department || ''} onChange={e => updateRow(origIdx, 'department', e.target.value)} className="text-xs h-8" /></td>}
                  {visibleCols.designation && <td className="px-2 py-1"><Input value={row.designation || ''} onChange={e => updateRow(origIdx, 'designation', e.target.value)} className="text-xs h-8" /></td>}
                  {visibleCols.actions && (
                    <td className="px-2 py-1 text-right">
                      <Button variant="ghost" size="sm" onClick={() => removeRow(origIdx)} className="text-red-500 text-xs h-7" data-testid={`staff-remove-${origIdx}`}>Remove</Button>
                    </td>
                  )}
                </tr>
              ))}
              {visible.length === 0 && (
                <tr><td colSpan={visibleColCount || 1} className="p-8 text-center text-gray-400">No staff matching filters</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      <p className="text-xs text-gray-400 mt-3">Tip: Removed or added rows are only saved when you click <strong>Save Changes</strong>. S.No. is auto-renumbered on save.</p>
    </div>
  );
}

// ============== NMC MANDATORY MANAGER ==============
function PdfSlot({ label, value, onChange, onLabelChange, removable, onRemove, idx }) {
  const { headers } = useAuth();
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'nmc');
      const res = await fetch(`${API}/api/cms/upload`, { method: 'POST', headers, body: fd });
      if (!res.ok) throw new Error();
      const data = await res.json();
      const url = `${API}/api/cms/files/${data.storage_path}`;
      onChange(url);
      toast.success('PDF uploaded');
    } catch { toast.error('Upload failed'); }
    setUploading(false);
    e.target.value = '';
  };

  return (
    <div className="flex flex-col md:flex-row md:items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100" data-testid={`pdf-slot-${idx ?? 'single'}`}>
      <div className="flex-1 min-w-0">
        {onLabelChange ? (
          <Input value={label} onChange={e => onLabelChange(e.target.value)} placeholder="Label" className="text-sm h-8 mb-1" data-testid={`pdf-label-${idx}`} />
        ) : (
          <p className="text-sm font-medium text-[#1A202C] mb-1">{label}</p>
        )}
        {value ? (
          <a href={value} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#4ECDC4] hover:underline truncate block">
            <FileText size={12} /><span className="truncate">{value.split('/').pop()}</span>
          </a>
        ) : (
          <p className="text-xs text-gray-400 italic">No PDF uploaded</p>
        )}
      </div>
      <div className="flex gap-2 shrink-0">
        <input ref={fileRef} type="file" accept=".pdf" onChange={handleFile} className="hidden" />
        <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`pdf-upload-btn-${idx ?? 'single'}`}>
          <Upload size={12} className="mr-1" />{uploading ? 'Uploading...' : value ? 'Replace' : 'Upload PDF'}
        </Button>
        {value && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)} className="text-red-500 text-xs">Clear</Button>
        )}
        {removable && (
          <Button type="button" variant="ghost" size="sm" onClick={onRemove} className="text-gray-400 hover:text-red-500 text-xs" data-testid={`pdf-remove-${idx}`}>✕</Button>
        )}
      </div>
    </div>
  );
}

function PdfListEditor({ title, items, onChange, defaultLabel = 'New Item' }) {
  const updateItem = (i, patch) => {
    const copy = [...items];
    copy[i] = { ...copy[i], ...patch };
    onChange(copy);
  };
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const addItem = () => onChange([...items, { label: defaultLabel, pdf: null }]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-[#1A202C]">{title}</h4>
        <Button type="button" variant="outline" size="sm" onClick={addItem} className="text-xs" data-testid={`add-${title.replace(/\s+/g, '-').toLowerCase()}`}>
          + Add Item
        </Button>
      </div>
      <div className="space-y-2">
        {items.map((item, i) => (
          <PdfSlot
            key={i}
            idx={`${title.replace(/\s+/g, '-').toLowerCase()}-${i}`}
            label={item.label || ''}
            onLabelChange={(val) => updateItem(i, { label: val })}
            value={item.pdf}
            onChange={(url) => updateItem(i, { pdf: url })}
            removable
            onRemove={() => removeItem(i)}
          />
        ))}
        {items.length === 0 && <p className="text-xs text-gray-400 italic text-center py-3">No items yet. Click "+ Add Item" to start.</p>}
      </div>
    </div>
  );
}

function SectionCard({ num, title, children }) {
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <span className="w-7 h-7 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center text-[#4ECDC4] text-xs font-bold">{num}</span>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">{children}</CardContent>
    </Card>
  );
}

function NMCMandatoryManager() {
  const { get, put } = useApi();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    get('/api/cms/pages/nmc_mandatory')
      .then(d => setData(d.sections ? d : { sections: {} }))
      .catch(() => toast.error('Failed to load NMC data'));
  }, [get]);

  const save = async () => {
    setSaving(true);
    try {
      await put('/api/cms/pages/nmc_mandatory', data);
      toast.success('NMC Mandatory Info saved');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const updateField = (key, value) => setData(d => ({ ...d, sections: { ...d.sections, [key]: value } }));

  if (!data) return <div className="animate-pulse text-gray-400">Loading NMC Mandatory sections...</div>;
  const s = data.sections || {};

  return (
    <div data-testid="cms-nmc-mandatory" className="space-y-5 max-w-5xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-[#1A202C]">NMC Mandatory Information</h2>
          <p className="text-sm text-gray-500 mt-1">Manage all 18 compliance sections &middot; upload PDFs directly to each slot</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#4ECDC4] hover:bg-[#3DBDB4]" data-testid="nmc-save-btn">
          <Save size={14} className="mr-1" />{saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>

      <SectionCard num="2" title="Teaching & Non-Teaching Staff">
        <p className="text-xs text-gray-500">Teaching staff is now managed on its own page: <code className="bg-gray-100 px-1 rounded">/admin/cms/teaching-staff</code></p>
        <PdfSlot label="Non-Teaching Staff" value={s.non_teaching_staff_pdf} onChange={url => updateField('non_teaching_staff_pdf', url)} />
      </SectionCard>

      <SectionCard num="3" title="Sanctioned Courses">
        <FieldEditor label="Course Description" type="textarea" rows={3} value={s.sanctioned_courses} onChange={v => updateField('sanctioned_courses', v)} />
      </SectionCard>

      <SectionCard num="4" title="Recognition Status">
        <FieldEditor label="Recognition Text" value={s.recognition_text} onChange={v => updateField('recognition_text', v)} />
        <FieldEditor label="NMC Verify URL" value={s.recognition_verify_url} onChange={v => updateField('recognition_verify_url', v)} />
      </SectionCard>

      <SectionCard num="5" title="University Details">
        <FieldEditor label="University Name" value={s.university_name} onChange={v => updateField('university_name', v)} />
        <FieldEditor label="University URL" value={s.university_url} onChange={v => updateField('university_url', v)} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldEditor label="Vice Chancellor Name" value={s.vc_name} onChange={v => updateField('vc_name', v)} />
          <FieldEditor label="Registrar Name" value={s.registrar_name} onChange={v => updateField('registrar_name', v)} />
          <FieldEditor label="VC Phone" value={s.vc_phone} onChange={v => updateField('vc_phone', v)} />
          <FieldEditor label="Registrar Phone" value={s.registrar_phone} onChange={v => updateField('registrar_phone', v)} />
          <FieldEditor label="VC Email" value={s.vc_email} onChange={v => updateField('vc_email', v)} />
          <FieldEditor label="Registrar Email" value={s.registrar_email} onChange={v => updateField('registrar_email', v)} />
        </div>
      </SectionCard>

      <SectionCard num="6" title="Students Details">
        <PdfListEditor title="MBBS Batches" items={s.mbbs_batches || []} onChange={v => updateField('mbbs_batches', v)} defaultLabel="MBBS Batch: YYYY-YYYY" />
        <PdfListEditor title="Stipend Details" items={s.stipend_details || []} onChange={v => updateField('stipend_details', v)} defaultLabel="Stipend Details YYYY-YY" />
      </SectionCard>

      <SectionCard num="7" title="Clinical Material">
        <PdfSlot label="Updated Clinical Material" value={s.clinical_material_pdf} onChange={url => updateField('clinical_material_pdf', url)} />
      </SectionCard>

      <SectionCard num="8" title="Results">
        <PdfListEditor title="Result Documents" items={s.results_items || []} onChange={v => updateField('results_items', v)} defaultLabel="Result" />
      </SectionCard>

      <SectionCard num="9" title="Committees">
        <PdfListEditor title="Committees" items={s.committees || []} onChange={v => updateField('committees', v)} defaultLabel="New Committee" />
      </SectionCard>

      <SectionCard num="10" title="CME, Conferences & Academic Activities">
        <PdfSlot label="CME / Academic Activities" value={s.cme_pdf} onChange={url => updateField('cme_pdf', url)} />
      </SectionCard>

      <SectionCard num="11" title="Awards & Achievements">
        <PdfListEditor title="Awards" items={s.awards_items || []} onChange={v => updateField('awards_items', v)} defaultLabel="Award" />
      </SectionCard>

      <SectionCard num="12" title="Time Table">
        <PdfListEditor title="Time Tables" items={s.timetable_items || []} onChange={v => updateField('timetable_items', v)} defaultLabel="Time Table" />
      </SectionCard>

      <SectionCard num="13" title="Faculty Attendance">
        <FieldEditor label="Faculty Attendance URL" value={s.faculty_attendance_url} onChange={v => updateField('faculty_attendance_url', v)} />
      </SectionCard>

      <SectionCard num="14" title="Teaching Details">
        <PdfListEditor title="Teaching Documents" items={s.teaching_items || []} onChange={v => updateField('teaching_items', v)} defaultLabel="Teaching Document" />
      </SectionCard>

      <SectionCard num="15" title="Infrastructure">
        <PdfListEditor title="Infrastructure Documents" items={s.infrastructure_items || []} onChange={v => updateField('infrastructure_items', v)} defaultLabel="Infrastructure Document" />
      </SectionCard>

      <SectionCard num="16" title="Student Feedback">
        <PdfListEditor title="Feedback Documents" items={s.feedback_items || []} onChange={v => updateField('feedback_items', v)} defaultLabel="Feedback" />
      </SectionCard>

      <SectionCard num="17" title="Research Publications">
        <PdfListEditor title="Publications" items={s.publications || []} onChange={v => updateField('publications', v)} defaultLabel="Publication" />
      </SectionCard>

      <SectionCard num="18" title="Citizen Charter">
        <PdfListEditor title="Charter Documents" items={s.citizen_charter_items || []} onChange={v => updateField('citizen_charter_items', v)} defaultLabel="Charter Document" />
      </SectionCard>

      <div className="sticky bottom-4 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#4ECDC4] hover:bg-[#3DBDB4] shadow-lg" data-testid="nmc-save-btn-bottom">
          <Save size={14} className="mr-1" />{saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>
    </div>
  );
}

// ============== ENQUIRIES MANAGER ==============
function EnquiriesManager() {
  const { get } = useApi();
  const { headers } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(() => { get('/api/enquiries').then(setEnquiries).catch(() => {}); }, [get]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    await fetch(`${API}/api/enquiries/${id}/status?new_status=${status}`, { method: 'PATCH', headers });
    toast.success('Status updated');
    load();
  };

  const deleteEnq = async (id) => {
    await fetch(`${API}/api/enquiries/${id}`, { method: 'DELETE', headers });
    toast.success('Deleted');
    load();
  };

  const STATUSES = ['new', 'contacted', 'converted', 'closed'];
  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: enquiries.filter(e => (e.status || 'new') === s).length }), {});
  const filtered = statusFilter === 'all' ? enquiries : enquiries.filter(e => (e.status || 'new') === statusFilter);

  const exportCsv = () => {
    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headersRow = ['Name', 'Email', 'Phone', 'Course', 'Source Page', 'Status', 'Message', 'Created At'];
    const rows = filtered.map(e => [e.name, e.email, e.phone, e.course, e.source_page, e.status, e.message, e.created_at]);
    const csv = [headersRow, ...rows].map(r => r.map(esc).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `enquiries_${statusFilter}_${stamp}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} enquir${rows.length === 1 ? 'y' : 'ies'}`);
  };

  const chipCls = (key) => `px-3 py-1.5 rounded-full text-xs font-medium border transition-colors whitespace-nowrap ${
    statusFilter === key
      ? 'bg-[#4ECDC4] text-white border-[#4ECDC4]'
      : 'bg-white text-gray-600 border-gray-200 hover:border-[#4ECDC4] hover:text-[#4ECDC4]'
  }`;

  return (
    <div data-testid="cms-enquiries">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-[#1A202C]">Enquiries</h2>
        <Button
          onClick={exportCsv}
          disabled={filtered.length === 0}
          className="bg-[#1A202C] hover:bg-[#2D3748] text-white gap-2"
          data-testid="enquiries-export-csv"
        >
          <Download size={16} />
          Export CSV {statusFilter !== 'all' && `(${statusFilter})`}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="enquiries-filter-bar">
        <button type="button" onClick={() => setStatusFilter('all')} className={chipCls('all')} data-testid="filter-all">
          All ({enquiries.length})
        </button>
        {STATUSES.map(s => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={chipCls(s)}
            data-testid={`filter-${s}`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)} ({counts[s] || 0})
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map(e => (
          <Card key={e.id} className="border-0 shadow-sm"><CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium">{e.name}</p>
                <p className="text-sm text-gray-500">{e.email} &middot; {e.phone}</p>
                <p className="text-sm text-gray-400 mt-1">Course: {e.course} &middot; From: {e.source_page}</p>
                {e.message && <p className="text-sm mt-2 text-gray-600">{e.message}</p>}
              </div>
              <div className="flex items-center gap-2">
                <select value={e.status} onChange={ev => updateStatus(e.id, ev.target.value)} className="text-xs border rounded px-2 py-1">
                  <option value="new">New</option><option value="contacted">Contacted</option><option value="converted">Converted</option><option value="closed">Closed</option>
                </select>
                <Button variant="ghost" size="sm" onClick={() => deleteEnq(e.id)} className="text-red-500 text-xs">Del</Button>
              </div>
            </div>
          </CardContent></Card>
        ))}
        {filtered.length === 0 && (
          <p className="text-center text-gray-400 py-12" data-testid="enquiries-empty">
            {statusFilter === 'all' ? 'No enquiries yet' : `No enquiries with status "${statusFilter}"`}
          </p>
        )}
      </div>
    </div>
  );
}

// ============== CAREERS CONFIG MANAGER ==============
function CareersConfigManager() {
  const { get, put } = useApi();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { get('/api/cms/careers/config').then(setCfg).catch(() => toast.error('Failed to load config')); }, [get]);

  const save = async () => {
    setSaving(true);
    try { const next = await put('/api/cms/careers/config', cfg); setCfg(next); toast.success('Careers config saved'); }
    catch { toast.error('Save failed'); }
    setSaving(false);
  };

  if (!cfg) return <div className="animate-pulse text-gray-400">Loading careers config...</div>;

  const updateList = (key, items) => setCfg(c => ({ ...c, [key]: items }));

  return (
    <div data-testid="cms-careers-config" className="max-w-4xl space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-[#1A202C]">Careers Config</h2>
          <p className="text-sm text-gray-500 mt-1">Edit dropdown options and HR notification email shown on the public careers form.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#4ECDC4] hover:bg-[#3DBDB4]" data-testid="careers-config-save">
          <Save size={14} className="mr-1" />{saving ? 'Saving...' : 'Save Config'}
        </Button>
      </div>

      <Card className="border-0 shadow-sm"><CardHeader><CardTitle className="text-base">HR Notification Email</CardTitle></CardHeader>
        <CardContent>
          <FieldEditor label="HR Email" value={cfg.hr_email} onChange={v => setCfg(c => ({ ...c, hr_email: v }))} />
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm"><CardHeader><CardTitle className="text-base">Applying for Category</CardTitle></CardHeader>
        <CardContent><ListEditor label="Categories" items={cfg.categories || []} onChange={v => updateList('categories', v)} /></CardContent>
      </Card>

      <Card className="border-0 shadow-sm"><CardHeader><CardTitle className="text-base">Departments (Teaching Staff)</CardTitle></CardHeader>
        <CardContent><ListEditor label="Departments" items={cfg.departments || []} onChange={v => updateList('departments', v)} /></CardContent>
      </Card>

      <Card className="border-0 shadow-sm"><CardHeader><CardTitle className="text-base">Designations (Teaching Staff)</CardTitle></CardHeader>
        <CardContent><ListEditor label="Designations" items={cfg.designations || []} onChange={v => updateList('designations', v)} /></CardContent>
      </Card>

      <Card className="border-0 shadow-sm"><CardHeader><CardTitle className="text-base">Highest Qualifications</CardTitle></CardHeader>
        <CardContent><ListEditor label="Qualifications" items={cfg.qualifications || []} onChange={v => updateList('qualifications', v)} /></CardContent>
      </Card>

      <Card className="border-0 shadow-sm"><CardHeader><CardTitle className="text-base">How heard about us?</CardTitle></CardHeader>
        <CardContent><ListEditor label="Options" items={cfg.hear_about_us || []} onChange={v => updateList('hear_about_us', v)} /></CardContent>
      </Card>
    </div>
  );
}

// ============== JOB APPLICATIONS MANAGER ==============
const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700' },
  { value: 'under_review', label: 'Under Review', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'shortlisted', label: 'Shortlisted', color: 'bg-[#4ECDC4]/15 text-[#2a857e]' },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700' },
  { value: 'contacted', label: 'Contacted', color: 'bg-gray-100 text-gray-700' },
];

function JobApplicationsManager() {
  const { headers } = useAuth();
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, filtered: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ category: '', department: '', status: '', search: '', date_from: '', date_to: '' });
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    try {
      const res = await fetch(`${API}/api/cms/careers/applications?${params}`, { headers });
      const data = await res.json();
      setApps(data.applications || []);
      setStats(data.stats || {});
    } catch { toast.error('Failed to load applications'); }
    setLoading(false);
  }, [filters, headers]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await fetch(`${API}/api/cms/careers/applications/${id}/status?new_status=${status}`, { method: 'PATCH', headers });
      toast.success('Status updated');
      load();
      if (selected?.id === id) setSelected(s => ({ ...s, status }));
    } catch { toast.error('Update failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this application permanently?')) return;
    try {
      await fetch(`${API}/api/cms/careers/applications/${id}`, { method: 'DELETE', headers });
      toast.success('Deleted');
      if (selected?.id === id) setSelected(null);
      load();
    } catch { toast.error('Delete failed'); }
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/api/cms/careers/applications/export`, { headers });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ssmch-applications-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch { toast.error('Export failed'); }
  };

  const viewDetails = async (id) => {
    try {
      const res = await fetch(`${API}/api/cms/careers/applications/${id}`, { headers });
      const data = await res.json();
      setSelected(data);
    } catch { toast.error('Failed to load details'); }
  };

  const fileUrl = (info) => info ? `${API}/api/cms/files/${info.storage_path}` : null;
  const statusMeta = (s) => STATUS_OPTIONS.find(o => o.value === s) || STATUS_OPTIONS[0];

  return (
    <div data-testid="cms-applications">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <h2 className="text-2xl font-bold text-[#1A202C]">Job Applications</h2>
          <p className="text-sm text-gray-500 mt-1">
            <span className="font-medium text-[#4ECDC4]">{stats.new || 0}</span> new &middot; {stats.filtered} showing &middot; {stats.total} total
          </p>
        </div>
        <Button onClick={exportCsv} className="bg-[#D4A84B] hover:bg-[#B88F35] text-white" data-testid="export-csv-btn">
          <Download size={14} className="mr-1" />Export CSV
        </Button>
      </div>

      <Card className="border-0 shadow-sm mb-4"><CardContent className="p-4 grid grid-cols-2 md:grid-cols-6 gap-2">
        <Input placeholder="Search name/email/mobile" value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} className="text-sm md:col-span-2" data-testid="app-search" />
        <Input placeholder="Category" value={filters.category} onChange={e => setFilters(f => ({ ...f, category: e.target.value }))} className="text-sm" />
        <Input placeholder="Department" value={filters.department} onChange={e => setFilters(f => ({ ...f, department: e.target.value }))} className="text-sm" />
        <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className="text-sm border rounded-md px-2 bg-white" data-testid="app-status-filter">
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div className="flex gap-1">
          <Input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} className="text-xs" title="From" />
          <Input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} className="text-xs" title="To" />
        </div>
      </CardContent></Card>

      {loading ? (
        <div className="text-center py-12 text-gray-400 animate-pulse">Loading applications...</div>
      ) : apps.length === 0 ? (
        <div className="text-center py-12 text-gray-400">No applications match the filters.</div>
      ) : (
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#1A202C] text-white">
                <tr>
                  <th className="px-3 py-2 text-left">Submitted</th>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Category</th>
                  <th className="px-3 py-2 text-left">Dept / Designation</th>
                  <th className="px-3 py-2 text-left">Qualification</th>
                  <th className="px-3 py-2 text-left">Contact</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {apps.map(a => (
                  <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`app-row-${a.id}`}>
                    <td className="px-3 py-2 text-xs text-gray-500">{a.created_at?.slice(0,10)}</td>
                    <td className="px-3 py-2 font-medium text-[#1A202C]">{a.full_name}</td>
                    <td className="px-3 py-2 text-xs">{a.category}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{[a.department, a.designation].filter(Boolean).join(' · ') || '—'}</td>
                    <td className="px-3 py-2 text-xs">{a.qualification}</td>
                    <td className="px-3 py-2 text-xs text-gray-600"><div>{a.email}</div><div>{a.mobile}</div></td>
                    <td className="px-3 py-2">
                      <select value={a.status} onChange={e => updateStatus(a.id, e.target.value)} className={`text-xs rounded px-2 py-1 border-0 ${statusMeta(a.status).color}`} data-testid={`app-status-${a.id}`}>
                        {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => viewDetails(a.id)} className="text-[#4ECDC4] text-xs" data-testid={`app-view-${a.id}`}>View</Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(a.id)} className="text-red-500 text-xs">Del</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setSelected(null)} data-testid="app-detail-drawer">
          <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#1A202C] text-white p-5 flex items-center justify-between z-10">
              <div>
                <h3 className="font-heading text-xl font-bold">{selected.full_name}</h3>
                <p className="text-xs text-gray-400">{selected.email} &middot; {selected.mobile}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelected(null)} className="text-white hover:bg-white/10">Close</Button>
            </div>
            <div className="p-6 space-y-5 text-sm">
              {selected.photo && (
                <div><img src={fileUrl(selected.photo)} alt="Applicant" className="w-32 h-32 rounded-lg object-cover border" /></div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-gray-400">Date of Birth</p><p>{selected.date_of_birth}</p></div>
                <div><p className="text-xs text-gray-400">Gender</p><p>{selected.gender}</p></div>
                <div><p className="text-xs text-gray-400">Father/Husband</p><p>{selected.father_or_husband_name}</p></div>
                <div><p className="text-xs text-gray-400">Alt Mobile</p><p>{selected.alternate_mobile || '—'}</p></div>
                <div className="col-span-2"><p className="text-xs text-gray-400">Current Address</p><p>{selected.current_address}</p></div>
                <div className="col-span-2"><p className="text-xs text-gray-400">Permanent Address</p><p>{selected.permanent_address || '—'}</p></div>
                <div><p className="text-xs text-gray-400">City / State / PIN</p><p>{selected.city}, {selected.state} — {selected.pincode}</p></div>
                <div><p className="text-xs text-gray-400">Status</p>
                  <select value={selected.status} onChange={e => updateStatus(selected.id, e.target.value)} className={`text-xs rounded px-2 py-1 border ${statusMeta(selected.status).color}`}>
                    {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="border-t pt-4">
                <p className="text-sm font-semibold mb-2">Professional</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs text-gray-400">Category</p><p>{selected.category}</p></div>
                  <div><p className="text-xs text-gray-400">Qualification</p><p>{selected.qualification}</p></div>
                  {selected.department && <div><p className="text-xs text-gray-400">Department</p><p>{selected.department}</p></div>}
                  {selected.designation && <div><p className="text-xs text-gray-400">Designation</p><p>{selected.designation}</p></div>}
                  <div><p className="text-xs text-gray-400">Experience</p><p>{selected.years_experience} yrs</p></div>
                  {selected.registration_number && <div><p className="text-xs text-gray-400">Registration</p><p>{selected.registration_number}</p></div>}
                  {selected.current_employer && <div className="col-span-2"><p className="text-xs text-gray-400">Current Employer</p><p>{selected.current_employer} {selected.current_designation ? `— ${selected.current_designation}` : ''}</p></div>}
                </div>
              </div>
              <div className="border-t pt-4">
                <p className="text-sm font-semibold mb-2">Documents</p>
                <div className="space-y-2">
                  {selected.resume && (
                    <a href={fileUrl(selected.resume)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 bg-[#4ECDC4]/5 hover:bg-[#4ECDC4]/10 rounded-lg text-[#4ECDC4]" data-testid="app-resume-link">
                      <FileText size={16} /><span className="flex-1 truncate">{selected.resume.original_filename}</span><Download size={14} />
                    </a>
                  )}
                  {(selected.certificates || []).map((c, i) => (
                    <a key={i} href={fileUrl(c)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-gray-700">
                      <FileText size={16} /><span className="flex-1 truncate">{c.original_filename}</span><Download size={14} />
                    </a>
                  ))}
                </div>
              </div>
              {selected.remarks && (
                <div className="border-t pt-4">
                  <p className="text-xs text-gray-400 mb-1">Remarks</p>
                  <p className="text-sm bg-gray-50 p-3 rounded-lg">{selected.remarks}</p>
                </div>
              )}
              {selected.hear_about_us && <p className="text-xs text-gray-400">Heard via: {selected.hear_about_us}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============== PAGE CONFIGS ==============
const PAGE_TITLES = {
  home: 'Home Page', about: 'About Page', academics: 'Academics Page',
  hospital: 'Hospital Page', admissions: 'Admissions Page', campus_life: 'Campus Life Page',
  faculty: 'Faculty Page', nmc_disclosures: 'NMC Disclosures Page',
  contact: 'Contact Page',
};

// ============== MAIN CMS COMPONENT ==============
export default function AdminCMS() {
  const { auth, login, logout } = useAuth();

  if (!auth) return <LoginScreen onLogin={login} />;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-cms">
      <Sidebar onLogout={logout} />
      <main className="ml-64 p-8 min-h-screen">
        <Routes>
          <Route path="/" element={<DashboardView />} />
          <Route path="settings" element={<SettingsEditor />} />
          {Object.entries(PAGE_TITLES).map(([key, title]) => (
            <Route key={key} path={`page/${key}`} element={<PageEditor pageKey={key} title={title} />} />
          ))}
          <Route path="news" element={<NewsManager />} />
          <Route path="gallery" element={<GalleryManager />} />
          <Route path="teaching-staff" element={<TeachingStaffManager />} />
          <Route path="nmc-mandatory" element={<NMCMandatoryManager />} />
          <Route path="uploads" element={<FileManager />} />
          <Route path="enquiries" element={<EnquiriesManager />} />
          <Route path="applications" element={<JobApplicationsManager />} />
          <Route path="careers-config" element={<CareersConfigManager />} />
        </Routes>
      </main>
    </div>
  );
}
